import { cn } from '@/lib/utils';
import {
  formatAgentLabel,
  formatDuration,
  elapsedSeconds,
} from '@/lib/format';
import { JobStatusBadge } from './JobStatusBadge';
import type { AgentRunDTO } from '@/types/db';

/**
 * `<AgentProgressRow>` — UI-SPEC § 3.3 single agent row in the detail view.
 *
 * Layout:
 * - md+: 5-col grid `[160px 92px 60px 1fr 72px]` for label · badge · step ·
 *   message · duration. Uses `md:contents` so the inner flex wrappers (which
 *   organize the mobile 2-row stack) collapse to direct grid children.
 * - <md: 2-row stack — row 1 is label · badge · step; row 2 is message · duration.
 *
 * The duration column shows live elapsed (when running) — the parent
 * `<AgentProgressList>` ticks every 1s and passes `liveElapsedSec` for
 * the running row. Completed rows compute end-minus-start once.
 *
 * Failed rows surface `error_message` into the message slot with a coral
 * tint so the operator sees the failure reason without drilling into the
 * report file.
 */
export interface AgentProgressRowProps {
  agentRun: AgentRunDTO;
  /** Live elapsed seconds for the running variant; null for non-running. */
  liveElapsedSec?: number | null;
}

export function AgentProgressRow({
  agentRun,
  liveElapsedSec,
}: AgentProgressRowProps) {
  const label = formatAgentLabel(agentRun.name);
  const stepText =
    agentRun.total_steps > 0
      ? `${agentRun.current_step}/${agentRun.total_steps}`
      : '—';

  const durSec =
    agentRun.status === 'running' && liveElapsedSec != null
      ? liveElapsedSec
      : elapsedSeconds(agentRun.started_at, agentRun.completed_at);
  const durText = agentRun.status === 'pending' ? '—' : formatDuration(durSec);

  const messageText =
    agentRun.status === 'failed'
      ? agentRun.error_message || agentRun.status_message || '—'
      : agentRun.status_message || '—';
  const messageClass =
    agentRun.status === 'failed' ? 'text-coral-100/90' : 'text-foam-50/70';

  const ariaLabel =
    `${label}: ${agentRun.status}` +
    (agentRun.total_steps > 0
      ? `, step ${agentRun.current_step} of ${agentRun.total_steps}`
      : '') +
    `, ${durText}`;

  return (
    <div
      role="listitem"
      aria-label={ariaLabel}
      className={cn(
        'border-b border-foam-50/10 last:border-b-0',
        'hover:bg-foam-50/[0.02]',
        // mobile (<md): 2-row stack
        'flex flex-col gap-2 py-3',
        // md+: 5-col grid per UI-SPEC § 3.3
        'md:grid md:grid-cols-[160px_92px_60px_1fr_72px] md:gap-4 md:items-center md:py-3',
      )}
    >
      {/* mobile row 1 / md cells 1 + 2 + 3 */}
      <div className="flex justify-between items-center gap-3 md:contents">
        <span className="text-sm font-semibold text-foam-50">{label}</span>
        <JobStatusBadge status={agentRun.status} size="sm" />
        <span className="text-sm tabular-nums text-foam-50/70">{stepText}</span>
      </div>
      {/* mobile row 2 / md cells 4 + 5 */}
      <div className="flex justify-between items-center gap-3 md:contents min-w-0">
        <span
          className={cn('text-sm truncate min-w-0', messageClass)}
          title={messageText}
        >
          {messageText}
        </span>
        <span className="text-sm font-mono tabular-nums text-foam-50/70 whitespace-nowrap">
          {durText}
        </span>
      </div>
    </div>
  );
}
